import {
  useQuery,
  DocumentNode,
  QueryResult,
  OperationVariables,
} from "@apollo/client";
import { useSubgraphSync } from "./useSubgraphSync";
import { QueryWithSyncOptions, SyncTarget } from "../types";

/**
 * Enhanced Apollo useQuery hook that includes subgraph synchronization tracking.
 * Combines GraphQL query execution with sync state monitoring.
 *
 * @param query - GraphQL query document
 * @param options - Combined Apollo query options and sync configuration
 *
 * @returns All Apollo query result properties plus sync state
 *
 * @example
 * ```tsx
 * const { data, loading, isIndexing, isSynced, progress } = useSubgraphQuery(
 *   GET_USER_QUERY,
 *   {
 *     variables: { id: '0x123' },
 *     targetBlockNumber: 1000000,
 *     syncConfig: { pollInterval: 2000 },
 *   }
 * );
 * ```
 */
export function useSubgraphQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(query: DocumentNode, options: QueryWithSyncOptions<TVariables> = {}) {
  const { syncConfig, targetBlockNumber, targetTimestamp, ...queryOptions } =
    options;

  const queryResult = useQuery<TData, TVariables>(query, queryOptions);
  const syncTarget: SyncTarget = {
    blockNumber: targetBlockNumber,
    timestamp: targetTimestamp,
  };

  const syncState = useSubgraphSync(syncTarget, syncConfig);

  return {
    ...queryResult,
    ...syncState,
    // Combined loading state
    isLoading: queryResult.loading || syncState.isIndexing,
    // Enhanced error state
    hasError: !!queryResult.error || syncState.isError,
  };
}
